<template>
  <form @submit.stop.prevent>
    <FlexLayout role="region">
      <BlockContainer>
        <!-- Use dynamic fieldsConfig instead of hardcoded REGISTRATION section -->
        <Input
          v-if="fieldsConfig.firstName && fieldsConfig.firstName.visible"
          id="firstName"
          :label="$t('field.firstName')"
          type="text"
          v-model="$v.form.firstName.$model"
          :error="$v.form.firstName.$dirty && $v.form.firstName.$error ? $t('validation.fieldWarningRequired') : ''"
          :required="fieldsConfig.firstName.required"
          :maxlength="fieldsConfig.firstName.maxLength"
          aria-describedby="customer-form-firstName"
          autofocus
        />
        <Input
          v-if="fieldsConfig.lastName && fieldsConfig.lastName.visible"
          id="lastName"
          :label="$t('field.lastName')"
          type="text"
          v-model="$v.form.lastName.$model"
          :error="$v.form.lastName.$dirty && $v.form.lastName.$error ? $t('validation.fieldWarningRequired') : ''"
          :required="fieldsConfig.lastName.required"
          :maxlength="fieldsConfig.lastName.maxLength"
          aria-describedby="customer-form-lastName"
          autofocus
        />
        <Input
          v-if="fieldsConfig.emailAddr && fieldsConfig.emailAddr.visible"
          id="emailAddr"
          :label="$t('field.email')"
          type="email"
          v-model="$v.form.emailAddr.$model"
          :error="$v.form.emailAddr.$dirty && $v.form.emailAddr.$error ? $t('validation.invalidEmail') : ''"
          :required="fieldsConfig.emailAddr.required"
          :maxlength="fieldsConfig.emailAddr.maxLength"
          aria-describedby="customer-form-emailAddr"
          autofocus
        />
        <Input
          v-if="fieldsConfig.streetAddress && fieldsConfig.streetAddress.visible"
          id="streetAddress"
          :label="$t('field.street')"
          type="text"
          v-model="$v.form.streetAddress.$model"
          :error="$v.form.streetAddress.$dirty && $v.form.streetAddress.$error ? $t('validation.fieldWarningRequired') : ''"
          :required="fieldsConfig.streetAddress.required"
          :maxlength="fieldsConfig.streetAddress.maxLength"
          aria-describedby="customer-form-streetAddress"
          autofocus
        />
        <Input
          v-if="fieldsConfig.postcode && fieldsConfig.postcode.visible"
          id="postcode"
          :label="$t('field.postalCode')"
          type="text"
          v-model="$v.form.postcode.$model"
          :error="$v.form.postcode.$dirty && $v.form.postcode.$error ? $t('validation.fieldWarningRequired') : ''"
          :required="fieldsConfig.postcode.required"
          :maxlength="fieldsConfig.postcode.maxLength"
          aria-describedby="customer-form-postcode"
          autofocus
        />
        <Input
          v-if="fieldsConfig.city && fieldsConfig.city.visible"
          id="city"
          :label="$t('field.city')"
          type="text"
          v-model="$v.form.city.$model"
          :error="$v.form.city.$dirty && $v.form.city.$error ? $t('validation.fieldWarningRequired') : ''"
          :required="fieldsConfig.city.required"
          :maxlength="fieldsConfig.city.maxLength"
          aria-describedby="customer-form-city"
          autofocus
        />
        <Dropdown v-if="countriesLoaded && fieldsConfig.countryId && fieldsConfig.countryId.visible"
                  v-model="$v.form.countryId.$model"
                  :label="$t('field.country')"
                  :options="countryOptions"
                  :placeholder="$t('system.placeholder.chooseOption')"
                  :required="fieldsConfig.countryId.required"
        />
        <Input
          v-if="fieldsConfig.telephoneNumber && fieldsConfig.telephoneNumber.visible"
          id="telephoneNumber"
          :label="$t('field.telephoneNumber')"
          type="text"
          v-model="$v.form.telephoneNumber.$model"
          :error="$v.form.telephoneNumber.$dirty && $v.form.telephoneNumber.$error ? $t('validation.fieldWarningRequired') : ''"
          :required="fieldsConfig.telephoneNumber.required"
          :maxlength="fieldsConfig.telephoneNumber.maxLength"
          aria-describedby="customer-form-telephoneNumber"
          autofocus
        />
        <template #actions>
          <Button type="submit" @click.stop="onSubmit" :disabled="$v.$invalid" variant="primary">{{ $t('editCustomer.button.save') }}</Button>
        </template>
      </BlockContainer>
    </FlexLayout>
  </form>
</template>

<script>
import Input from "@/components/forms/Input.vue";
import Dropdown from "@/components/forms/Dropdown.vue";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import { isRequired, isValidEmail } from '@/utils/validationUtils';
import {customerService} from "@/services/api/customerService";
import Button from "@/components/actions/Button.vue";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";

export default {
  emits:['submit'],
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        streetAddress: '',
        postcode: '',
        city: '',
        countryId: '',
        telephoneNumber: '',
        emailAddr: ''
      },
      countriesLoaded: false,
      countries: [],
    }
  },
    computed: {
      fieldsConfig() {
        return this.$config.CUSTOMER_DATA.FIELDS;
      },
      countryOptions() {
        if (!Array.isArray(this.countries)) return [];
        return this.countries.map(c => ({ value: c.id, label: c.name }));
      },
    },
    components:{
      FlexLayout,
      Button,
      BlockContainer,
      Dropdown: Dropdown,
      Input,
    },
    validations() {
      const fields = this.fieldsConfig || {};
      return {
        form: {
          firstName: { required: v => isRequired(v, fields.firstName) },
          lastName: { required: v => isRequired(v, fields.lastName) },
          streetAddress: { required: v => isRequired(v, fields.streetAddress) },
          postcode: { required: v => isRequired(v, fields.postcode) },
          city: { required: v => isRequired(v, fields.city) },
          countryId: { required: v => isRequired(v, fields.countryId) },
          telephoneNumber: { required: v => isRequired(v, fields.telephoneNumber) },
          emailAddr: { required: v => isRequired(v, fields.emailAddr), email: isValidEmail },
        }
      }
    },
  methods: {
    async onSubmit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.makeToast('danger', this.$t('notification.fillAllRequiredFields'));
        return;
      }

      const result = await customerService.editCustomer(this.form);

      if (result.exception !== undefined || (result.errorCode !== undefined && result.errorCode !== 0)) {
        this.makeToast('danger', this.$t('editCustomer.messages.error.onUpdate'));
        return;
      }

      this.makeToast('success', this.$t('editCustomer.messages.info.onUpdate'));
      this.$emit('submit');
    },
      async fillForm() {
        const result = await customerService.getCustomer();

        this.form.firstName = result.firstName;
        this.form.lastName = result.lastName;
        this.form.streetAddress = result.streetAddress;
        this.form.postcode = result.postcode;
        this.form.city = result.city;
        this.form.countryId = result.countryId;
        this.form.telephoneNumber = result.telephoneNumber;
        this.form.emailAddr = result.emailAddr;

        this.countries = result.countries;
        this.countriesLoaded = true;
      },
    },
  async created(){
    await this.fillForm();
  },
}
</script>
